// Copyright (c) StrangeBee 2023

import angular from 'angular';
import React, { useState, useEffect, ReactElement, useCallback } from 'react';
import UiBootstrap from 'angular-ui-bootstrap';
import { fang } from '@Helpers/observable';

import './BootstrapShim.scss';

interface InjectorType {
    get: (...args: any[]) => any;
}

let $injector: InjectorType = {
    get: () => {
        return;
    },
};

interface StateProps {
    scope: {
        $digest: (...args: any[]) => any;
    };
}

function ngOrchestrator(componentName: string): () => ReactElement {
    const kebabName = (componentName.match(/[A-Z][a-z]*|^[a-z]*/g) || []).join('-').toLowerCase();

    function ReactComponent(): ReactElement {
        const [state, setState] = useState<StateProps>();
        const [loaded, setLoaded] = useState(false);

        const compile = useCallback(
            (element: ReactElement): void => {
                if (loaded || !state?.scope) {
                    return;
                }
                const linkTo = $injector.get('$compile')(element);
                linkTo(state.scope);
                state.scope.$digest();
                setLoaded(true);
            },
            [loaded, state?.scope],
        );

        useEffect(() => {
            const _state = {
                scope: $injector.get('$rootScope').$new(true),
            };
            setState(_state);
            return (): void => {
                _state.scope.$destroy();
            };
        }, []);

        return React.createElement(kebabName, { ref: compile });
    }

    return ReactComponent;
}

interface TemplateShimProps {
    template: string;
    content: string;
    artifact?: any;
    success?: boolean;
    name?: string;
}

function ngApp({
    name = 'templateAngular',
    template,
    content,
    artifact,
    success = true,
}: TemplateShimProps): (...args: any[]) => any {
    const capitelizeName = name[0].toUpperCase() + name.substring(1);
    const componentName = `comp${capitelizeName}`;
    const controllerName = `Controller${capitelizeName}`;

    const TemplateAngular = {
        template,
        controller: controllerName,
    };

    angular
        .module(`App${name}`, [UiBootstrap])
        .filter('fang', function (): (...args: any[]) => any {
            return function (value?: string): string {
                if (!value) {
                    return '';
                }

                return fang(value);
            };
        })
        .filter('ellipsis', function () {
            return function (input: any, total: any) {
                if (input.length > total) {
                    return input.slice(0, total) + '...';
                }
                return input;
            };
        })
        .component(componentName, TemplateAngular)
        .controller(controllerName, [
            '$scope',
            ($scope: any): void => {
                $scope.content = content;
                $scope.artifact = artifact;
                $scope.success = success;
            },
        ])
        .run([
            '$injector',
            function (_$injector: any): void {
                $injector = _$injector;
            },
        ]);

    angular.bootstrap(document.createElement('div'), [`App${name}`]);
    return ngOrchestrator(componentName);
}

const TemplateShim = ({
    template,
    content,
    artifact,
    name = 'templateAngular',
    success = true,
}: TemplateShimProps): ReactElement | null => {
    try {
        const NgApp = ngApp({ name, template, content, artifact, success });
        return <NgApp />;
    } catch {
        return null;
    }
};
export default React.memo(TemplateShim);
